/**
 * LocationDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 3 tabs:
 * Config, Refine, Variants
 *
 * Time of day & weather are now in the Config tab's LocationDesigner flyout.
 *
 * Width: 600px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Position, NodeProps, useEdges, useReactFlow, Handle } from 'reactflow'
import { AnimatePresence, motion } from 'framer-motion'
import { getThumbnailUrl, getAssetUrl, type LocationDesignerNodeData } from '@ponpon/api-client'
import { useExecuteNodeAction } from '../../../../hooks/useExecuteNodeAction'
import { useCanvasOptional } from '../../CanvasContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  LOCATION_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
    RefineTabContent,
  VariantsTabContent,
  useDesignerVariantSelection,
  useNodeActions,
  ShimmerPlaceholder,
  TypewriterText,
  useSequencedLoading,
  useBottomPanelAutoOpen,
  createHandlesFromSchema,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  type HistoryEntry,
} from '../shared'
import { useFeedbackMode } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { cn } from '../../../../lib/utils'
import { MessageSquarePlus, BookmarkPlus, Palette } from 'lucide-react'
import locationDesignerSchema from '../../../../../../../schemas/nodes/locationDesigner.json'

export const TIME_OF_DAY_OPTIONS = ['dawn', 'day', 'dusk', 'night']
export const WEATHER_OPTIONS = ['clear', 'cloudy', 'rainy', 'foggy', 'snowy', 'stormy']

import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NODE_BG_COLORS, NODE_BG_SWATCH_COLORS, resolveNodeBgColor } from '../shared/nodeBgColors'

export type { LocationDesignerNodeData }

const HANDLES = createHandlesFromSchema(locationDesignerSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  outputs: {
    out_name: { position: Position.Right, style: { top: '35%' } },
    out_metadata: { position: Position.Right },
  },
})

const agentColor = AGENT_COLORS.location

const LocationDesignerNode = memo(function LocationDesignerNode(
  props: NodeProps<LocationDesignerNodeData>
) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    in_name = '',
    in_style,
    in_time_of_day = 'day',
    in_weather = 'clear',
    in_model = 'openai-gpt-image-2',
    in_description = '',
    out_location_image,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes, getEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId: id })
  const setHoveredNodeId = useSetHoveredNodeId()
  const [isHovered, setIsHovered] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  // Image URL resolution
  const mainImage = out_location_image?.images?.main
  let imagePath: string | undefined
  if (mainImage?.history?.length) {
    const idx = data.picked_variant_index ?? mainImage.picked_index ?? 0
    imagePath = mainImage.history?.[idx]?.result?.local_path
  }
  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId ? getThumbnailUrl(projectId, imagePath, 0, cacheBust) : null
  const fullImageUrl = imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'

  const {
    showSubtitleShimmer,
    subtitleActive,
    onSubtitleComplete,
  } = useSequencedLoading({ isRunning: showSpinner })

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // History state
  const mainImageHistory = out_location_image?.images?.main
  const historyEntries = useMemo(
    (): HistoryEntry[] => mainImageHistory?.history ?? [],
    [mainImageHistory?.history]
  )
  const pickedIndex = data.picked_variant_index ?? mainImageHistory?.picked_index ?? 0
  const batchPending = showSpinner ? (data.batch_pending ?? 0) : 0

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    status,
  })

  // Shared variant selection (writes picked_variant_index + out_image, not the JSON blob)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    historyEntries,
    outputFieldName: 'out_location_image',
  })

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: newDesc })
    },
    [id, setNodes, canvas]
  )

  // Config generate handler — useExecuteNodeAction adds 1s cooldown
  // and routes insufficient_sparks 402 to the workspace modal.
  const { runExecute, isLocked: generateLocked, cancelExecution, isCancelling } = useExecuteNodeAction({
    projectId,
    nodeId: id,
    isRunning,
  })
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      if (showSpinner) return
      runExecute(retryCount)
    },
    [runExecute, showSpinner],
  )

  // Refine handler
  const onRefineSubmit = useCallback(() => {
    handleRefine()
  }, [handleRefine])

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      canvas?.updateNodeData(id, { in_style: styleId })
    },
    [id, setNodes, canvas]
  )

  // Time of day / weather change handlers for Config tab flyout
  const handleTimeOfDayChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_time_of_day: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_time_of_day: value })
    },
    [id, setNodes, canvas]
  )

  const handleWeatherChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_weather: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_weather: value })
    },
    [id, setNodes, canvas]
  )

  // Model change handler
  const handleModelChange = useCallback(
    (model: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_model: model } } : n
        )
      )
      canvas?.updateNodeData(id, { in_model: model })
    },
    [id, setNodes, canvas]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [id, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      canvas?.updateNodeData(id, { in_name: newName })
    },
    [id, setNodes, canvas],
  )

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      canvas?.updateNodeData(id, { in_bg_color: colorKey })
      setShowColorPicker(false)
    },
    [id, setNodes, canvas],
  )

  // Download handler — standardized filename via shared helper.
  const onDownload = useCallback(() => {
    if (!imagePath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: getEdges(),
      nodeId: id,
      assetPath: imagePath,
      mediaType: 'image',
      variantHistory: historyEntries,
      pickedIndex,
    })
    handleDownload(imagePath, filename)
  }, [imagePath, handleDownload, getNodes, getEdges, id, historyEntries, pickedIndex])

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'bg-color',
        label: 'Background Color',
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
        group: true,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary]
  )

  // Border color state machine
  const computedBorderStyle: React.CSSProperties = {}
  if (selected) {
    computedBorderStyle.borderColor = agentColor.active
    computedBorderStyle.boxShadow = agentColor.glow
  } else if (showSpinner) {
    computedBorderStyle.borderColor = agentColor.active
  } else if (isHovered) {
    computedBorderStyle.borderColor = agentColor.focus
  } else {
    computedBorderStyle.borderColor = agentColor.hover
  }

  // Panel visibility
  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name} fallback="Untitled Location" typeIcon={NODE_TYPE_ICON.locationDesigner} onRename={handleRename} bgColor={data.in_bg_color && data.in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[data.in_bg_color] : undefined} />

        {/* Color picker panel */}
        {showColorPicker && (
          <div
            ref={colorPickerRef}
            data-testid="location-color-picker"
            style={{
              position: 'absolute',
              top: -52,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60,
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
              {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
                const isActive = (data.in_bg_color ?? 'default') === key
                return (
                  <button
                    key={key}
                    aria-label={NODE_BG_COLORS[key]?.label ?? key}
                    title={NODE_BG_COLORS[key]?.label ?? key}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleBgColorChange(key)
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: swatchColor,
                      border: isActive ? '2px solid white' : '2px solid transparent',
                      cursor: 'pointer',
                      flexShrink: 0,
                      outline: 'none',
                      boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Main Card -- 600px for landscape locations */}
        <div
          data-testid="location-designer-node"
          className={cn(
            'relative rounded-[24px] border transition-all duration-200 overflow-hidden cursor-pointer',
            showSpinner && 'animate-node-shimmer'
          )}
          style={{ width: 600, backgroundColor: resolveNodeBgColor(data.in_bg_color), ...computedBorderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(id)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Image Area */}
          <div className="relative group p-1.5">
            <AnimatePresence mode="wait">
              {imageUrl && (
                <motion.img
                  key={imagePath}
                  src={imageUrl}
                  alt="Location"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className={cn(
                    'w-full aspect-video object-cover block rounded-[20px]',
                    showSpinner && 'blur-sm brightness-50 transition-all duration-300'
                  )}
                />
              )}
            </AnimatePresence>

            {showSpinner && imageUrl && (
              <div className="absolute inset-1.5 rounded-[20px] flex flex-col items-center justify-center">
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-sm text-white mt-3 font-medium">
                  {isRunning
                    ? 'Refining location'
                    : isPendingCost
                      ? 'Waiting for cost approval...'
                      : 'Queued — waiting for dependencies...'}
                </span>
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelExecution() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title="Stop"
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">Stop</span>
                  </button>
                )}
              </div>
            )}

            {!imageUrl && showSpinner && (
              <div className="w-full aspect-video bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center">
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-xxs text-text-muted mt-3 text-center">
                  {progressMessage ||
                    (isRunning
                      ? `Creating${data.in_name ? ` ${data.in_name}` : ' location'}...`
                      : isPendingCost
                        ? 'Waiting for cost approval...'
                        : 'Queued — waiting for dependencies...')}
                </span>
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelExecution() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title="Stop"
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">Stop</span>
                  </button>
                )}
              </div>
            )}

            {!imageUrl && !showSpinner && (
              <div className="w-full aspect-video bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center">
                <svg
                  className="w-10 h-10 text-text-muted/20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">No location yet</span>
              </div>
            )}

            {imageUrl && !showSpinner && (
              <div className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="pt-2 px-6 pb-6">
            <span className="text-xs font-medium" style={{ color: agentColor.active }}>
              Location
            </span>

            {/* Description -- editable */}
            <div className="mt-2 h-[60px]">
              {showSubtitleShimmer ? (
                <div className="space-y-1.5">
                  <ShimmerPlaceholder width="100%" height={14} />
                  <ShimmerPlaceholder width="80%" height={14} />
                </div>
              ) : showSpinner && in_description ? (
                <TypewriterText
                  text={in_description}
                  isActive={subtitleActive}
                  onComplete={onSubtitleComplete}
                  className="text-sm text-muted-foreground leading-relaxed line-clamp-3"
                  as="p"
                />
              ) : (
                <textarea
                  data-testid="location-description-input"
                  aria-label="Location description"
                  value={in_description || ''}
                  placeholder="Describe this location..."
                  onChange={handleDescriptionChange}
                  className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
                  rows={3}
                />
              )}
            </div>
          </div>

          {/* Quick Info Bar -- 2 columns: Config | Variants */}
          <div className="grid grid-cols-2 border-t border-border-subtle px-6">
            {/* Config Column */}
            <button
              data-testid="quick-info-config"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('config')
              }}
              className="py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle pr-3"
            >
              <span className="text-sm font-medium text-foreground truncate">
                {`${in_time_of_day.charAt(0).toUpperCase() + in_time_of_day.slice(1)} · ${in_weather.charAt(0).toUpperCase() + in_weather.slice(1)}`}
              </span>
              {currentRefs.length > 0 && (
                <div className="flex items-center gap-1">
                  {currentRefs.slice(0, 3).map((ref, i) => {
                    const thumbUrl = projectId
                      ? getThumbnailUrl(projectId, ref.assetPath, 32, cacheBust)
                      : null
                    return thumbUrl ? (
                      <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : null
                  })}
                </div>
              )}
            </button>

            {/* Variants Column */}
            <button
              data-testid="quick-info-variants"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('variants')
              }}
              className="px-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag"
            >
              <span className="text-sm font-medium text-foreground">Variants</span>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      historyEntries.length > 0 ? agentColor.active : 'var(--text-muted)',
                  }}
                />
                <span className="text-sm font-medium text-foreground">
                  {historyEntries.length}
                </span>
              </div>
            </button>
          </div>

          {/* Error footer */}
          {error && (
            <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/30">
              <span className="text-xxs text-destructive line-clamp-2 cursor-help" title={error}>
                {sanitizeNodeError(error)}
              </span>
            </div>
          )}
        </div>

        {/* Unified Bottom Panel */}
        <UnifiedBottomPanel
          tabs={LOCATION_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="location"
                  prompt={in_description}
                  onPromptChange={handlePromptChange}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  model={in_model}
                  onModelChange={handleModelChange}
                  timeOfDay={in_time_of_day.charAt(0).toUpperCase() + in_time_of_day.slice(1)}
                  weather={in_weather.charAt(0).toUpperCase() + in_weather.slice(1)}
                  onTimeOfDayChange={(t) => handleTimeOfDayChange(t.toLowerCase())}
                  onWeatherChange={(w) => handleWeatherChange(w.toLowerCase())}
                  isRunning={showSpinner || generateLocked}
                  onGenerate={handleConfigGenerate}
                  onCancel={isRunning ? cancelExecution : undefined}
                  isCancelling={isCancelling}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={onRefineSubmit}
                  isRunning={showSpinner}
                  nodeType="locationDesigner"
                  selectedStyleId={in_style}
                  onStyleChange={handleStyleChange}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  accentColor="green"
                  aspectRatio="16/9"
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

LocationDesignerNode.displayName = 'LocationDesignerNode'
export default LocationDesignerNode
